import { useEffect, useMemo, useState } from "react";
import CryptoModal from "../CryptoModal";
import { CRYPTO_MODAL_WRAPPER_CLASS } from "../modalLayout";
import ProcessingStep from "../BuySellModal/ProcessingStep";
import SwapEntryStep from "./SwapEntryStep";
import ConfirmSwapStep from "./ConfirmSwapStep";
import SwapSuccessStep from "./SwapSuccessStep";
import ErrorMessage from "../../../components/Common/ErrorMessage";
import { ChevronRightIcon, SwapIcon } from "../../icons";
import AddAccountModal from "../../../components/Modals/AddAccount/AddAccountModal";

const SwapModal = ({ 
  isOpen, 
  onClose, 
  setLoadData, 
  cryptoAccounts = [],
  selectedfromAsset = null
}) => {
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fromAsset, setFromAsset] = useState(null);
  const [toAsset, setToAsset] = useState(null);
  const [fromAmount, setFromAmount] = useState("");
  const [activePicker, setActivePicker] = useState(null);
  const [error, setError] = useState("");

  const hasInsufficientAccounts = (cryptoAccounts?.length ?? 0) <= 1;

  // Resolve matching account from cryptoAccounts
  const getInitialFromAsset = () => {
    if (!selectedfromAsset) {
      // Auto-select if user only has 1 account
      return cryptoAccounts.length === 1 ? cryptoAccounts[0] : null;
    }

    return selectedfromAsset;
  };

  const resetState = () => {
    setStep(1);
    setIsProcessing(false);
    setToAsset(null);
    setFromAmount("");
    setActivePicker(null);
    setError("");
  };

  const handleClose = () => {
    resetState();
    setFromAsset(null);
    onClose();
  };

  // Sync fromAsset when modal opens or selected asset changes
  useEffect(() => {
    if (isOpen) {
      setFromAsset(getInitialFromAsset());
    } else {
      resetState();
    }
    // eslint-disable-next-line
  }, [isOpen, selectedfromAsset, cryptoAccounts]);

  const handleBack = () => {
    if (isProcessing) return;

    if (step === 3) {
      handleClose();
      return;
    }

    if (activePicker) {
      setActivePicker(null);
      return;
    }

    if (step === 2) {
      setStep(1);
      return;
    }

    handleClose();
  };

  const handleAssetSelect = (asset) => {
    if (activePicker === "from") {
      setFromAsset(asset);
      if (toAsset?.id === asset.id) setToAsset(null);
    } else if (activePicker === "to") {
      setToAsset(asset);
      if (fromAsset?.id === asset.id) setFromAsset(null);
    }
    setActivePicker(null);
  };

  const handleSwapDirection = () => {
    if (!toAsset) return;
    setFromAsset(toAsset);
    setToAsset(fromAsset);
    setActivePicker(null);
  };

  const toAmount = useMemo(() => {
    if (!fromAsset || !toAsset) return 0;
    const fromVal = parseFloat(fromAmount) || 0;
    return (fromVal * fromAsset.unit_price_usd) / toAsset.unit_price_usd;
  }, [fromAmount, fromAsset, toAsset]);

  // Auto-select if there is a single available account for context
  useEffect(() => {
    if (cryptoAccounts.length === 1 && !fromAsset) {
      setFromAsset(cryptoAccounts[0]);
    }
  }, [cryptoAccounts, fromAsset]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) resetState();
    // eslint-disable-next-line
  }, [isOpen]);

  const renderSingleAccountNotice = () => (
    <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
      <div className="mb-4 flex items-center justify-center rounded-full bg-surface-secondary text-2xl">
        <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-[#4ab933] text-[#4ab933] bg-surface-secondary">
            <SwapIcon className={"h-10 w-10 !rotate-180"} />
        </div>
      </div>
      <h3 className="mb-2 font-outfit text-lg font-semibold text-text-primary">
        At least two accounts needed
      </h3>
      <p className="mb-6 max-w-xs text-sm text-text-secondary">
        You need at least two crypto accounts to perform a swap. Add or enable another crypto asset to continue.
      </p>
      <div className="flex w-full flex-col gap-3">
        <AddAccountModal crypto={true}>
          <button
            type="button"
            className="flex h-12 mb-2 w-full shrink-0 items-center justify-center rounded-[10px] bg-[#4ab933] 
            text-sm font-semibold leading-[21px] text-white custom-transition hover:bg-[#3fa82c] 
            disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add Crypto Account
          </button>

        </AddAccountModal>
        <button
          type="button"
          onClick={handleClose}
          className="w-full rounded-lg bg-surface-secondary py-3 font-medium text-text-primary transition hover:bg-opacity-80"
        >
          Close
        </button>
      </div>
    </div>
  );

  const renderStep = () => {
    if (hasInsufficientAccounts) {
      return renderSingleAccountNotice();
    }

    if (isProcessing) {
      return <ProcessingStep />;
    }

    switch (step) {
      case 1:
        return (
          <SwapEntryStep
            fromAsset={fromAsset}
            toAsset={toAsset}
            fromAmount={fromAmount}
            activePicker={activePicker}
            cryptoAccounts={cryptoAccounts}
            onFromAmountChange={setFromAmount}
            onOpenPicker={(picker) =>
              setActivePicker((current) => (current === picker ? null : picker))
            }
            onAssetSelect={handleAssetSelect}
            onSwapDirection={handleSwapDirection}
            onContinue={() => setStep(2)}
          />
        );
      case 2:
        return (
          <ConfirmSwapStep
            fromAsset={fromAsset}
            toAsset={toAsset}
            fromAmount={fromAmount}
            toAmount={toAmount}
            setIsProcessing={setIsProcessing}
            setStep={setStep}
            setError={setError}
          />
        );
      case 3:
        return (
          <SwapSuccessStep
            toAsset={toAsset}
            toAmount={toAmount}
            fromAsset={fromAsset}
            fromAmount={fromAmount}
            onCheckAssets={() => {
              setLoadData((prev) => !prev);
              handleClose();
            }}
          />
        );
      default:
        return null;
    }
  };

  const isFormStep = step === 1 && !isProcessing && !hasInsufficientAccounts;

  return (
    <CryptoModal
      isOpen={isOpen}
      onClose={handleClose}
      wrapperClassName={CRYPTO_MODAL_WRAPPER_CLASS}
      panelClassName={`!gap-0 !py-0 ${
        isFormStep ? "!overflow-visible" : "!overflow-hidden"
      }`}
      mobileFullScreen={true}
      showCancel={true}
      headerClassName="h-12 shrink-0 px-6 sm:pt-6 !pt-6"
      headerTabs={
        <>
          <button
            type="button"
            onClick={handleBack}
            className="block rounded-full custom-transition hover:bg-option-hover"
            aria-label="Go back"
          >
            <ChevronRightIcon className="rotate-180 text-text-primary" />
          </button>

          <h2 className="font-outfit text-lg font-semibold leading-7 text-text-primary max-sm:pl-2">
            Swap Crypto
          </h2>
        </>
      }
    >
      <div
        className={`flex min-h-0 w-full flex-1 flex-col ${
          isFormStep ? "overflow-visible" : "overflow-hidden"
        }`}
      >
        <div className="px-8">
          {error && <ErrorMessage message={error} onClose={() => setError("")} />}
        </div>

        {renderStep()}
      </div>
    </CryptoModal>
  );
};

export default SwapModal;